import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@components/atoms';
import * as React from 'react';
import { FilterChip } from './filter-chip';
import type { FilterState } from './filter-menu';

const filterChipsContainerCss = (visible: boolean) => ({
	opacity: `${visible ? '1' : '0'}`,
	overflow: 'hidden',
	transition: 'opacity 0.2s ease-out',
});

interface FilterOptionChange {
	groupKey: string;
	isSelected: boolean;
	label: string;
	optionKey: string;
}

interface FilterChipsContainerProps {
	filters: Record<string, FilterState>;
	onRemoveFilter: (update: FilterOptionChange) => void;
}

interface VisibleFilters {
	key: string;
	label: string;
	onRemove: () => void;
	value: string;
}

const CHIP_HORIZONTAL_PADDING = 16;
const CHIP_BORDER = 2;
const CHIP_GAP = 8;
const CLOSE_BUTTON_WIDTH = 16;
const MORE_LABEL_WIDTH = 64;
const MAX_CHIP_WIDTH = 180;
// Width correction factors based on manual analysis of `measureTextWidth`
const WIDTH_CORRECTION_MULTIPLIER = 1.1;
const WIDTH_CORRECTION_ADDITIVE = 10;
// Buffer to account for slight miscalculations or rounding errors
const SAFETY_BUFFER = 5;

export const FilterChipsContainer: React.FC<FilterChipsContainerProps> = ({ filters, onRemoveFilter }) => {
	const chipWidthCache = React.useRef<Map<string, number>>(new Map());
	const containerRef = React.useRef<HTMLDivElement>(null);
	const [hiddenCount, setHiddenCount] = React.useState(0);
	const measureRef = React.useRef<HTMLDivElement>(null);
	const [visibleFilters, setVisibleFilters] = React.useState<VisibleFilters[]>([]);
	const [isVisible, setIsVisible] = React.useState<boolean>(false);

	const totalFilters = React.useMemo(
		() => Object.values(filters).reduce((sum, filter) => sum + filter.values.size, 0),
		[filters]
	);

	const measureTextWidth = React.useCallback((text: string): number => {
		if (!measureRef.current) return 0;

		const cached = chipWidthCache.current.get(text);
		if (cached !== undefined) return cached;

		measureRef.current.textContent = text;

		const textWidth = measureRef.current.offsetWidth;

		// Apply correction factors based on analysis of actual vs. calculated widths
		const correctedWidth = textWidth * WIDTH_CORRECTION_MULTIPLIER + WIDTH_CORRECTION_ADDITIVE;

		// Calculate total width with padding and ensure it doesn't exceed MAX_CHIP_WIDTH
		const totalWidth = Math.min(
			correctedWidth + CHIP_HORIZONTAL_PADDING + CHIP_BORDER + CLOSE_BUTTON_WIDTH,
			MAX_CHIP_WIDTH
		);

		chipWidthCache.current.set(text, totalWidth);
		return totalWidth;
	}, []);

	const fitFilters = React.useCallback(
		(availableWidth: number): VisibleFilters[] => {
			let currentWidth = 0;
			const filtersToShow: VisibleFilters[] = [];

			outer: for (const [key, filter] of Object.entries(filters)) {
				for (const value of filter.values) {
					const label = filter.labels.get(value) || value;
					const chipWidth = measureTextWidth(label) + CHIP_GAP;

					if (currentWidth + chipWidth <= availableWidth) {
						filtersToShow.push({
							key,
							value,
							label,
							onRemove: () =>
								onRemoveFilter({
									groupKey: key,
									isSelected: false,
									label,
									optionKey: value,
								}),
						});
						currentWidth += chipWidth;
					} else {
						break outer;
					}
				}
			}

			return filtersToShow;
		},
		[filters, measureTextWidth, onRemoveFilter]
	);

	const calculateVisibleFilters = React.useCallback(() => {
		const container = containerRef.current;
		if (!container) return;

		const containerWidth = container.offsetWidth;

		// First try to fit every chip without reserving room for the "+X more" label, so the label is
		// only introduced when chips actually overflow.
		const allChips = fitFilters(containerWidth - SAFETY_BUFFER);
		const filtersToShow =
			allChips.length === totalFilters
				? allChips
				: fitFilters(containerWidth - MORE_LABEL_WIDTH - CHIP_GAP - SAFETY_BUFFER);

		setIsVisible(filtersToShow.length > 0);
		setVisibleFilters(filtersToShow);
		setHiddenCount(totalFilters - filtersToShow.length);
	}, [fitFilters, totalFilters]);

	React.useEffect(() => {
		const container = containerRef.current;
		if (!container) return;

		calculateVisibleFilters();

		const resizeObserver = new ResizeObserver(() => {
			chipWidthCache.current.clear();
			calculateVisibleFilters();
		});

		resizeObserver.observe(container);
		return () => resizeObserver.disconnect();
	}, [calculateVisibleFilters]);

	React.useEffect(() => {
		calculateVisibleFilters();
	}, [filters, calculateVisibleFilters]);

	const hiddenFilters = React.useMemo(() => {
		if (hiddenCount === 0) return '';

		return Object.entries(filters)
			.flatMap(([key, filter]) =>
				Array.from(filter.values)
					.filter(value => !visibleFilters.some(vf => vf.key === key && vf.value === value))
					.map(value => filter.labels.get(value) || value)
			)
			.join(', ');
	}, [filters, hiddenCount, visibleFilters]);

	return (
		<React.Fragment>
			<div
				ref={containerRef}
				data-aid="data-grid-action-bar-filter-chip-container"
				className={`flex items-center gap-2 overflow-hidden relative filter-chips-container ${isVisible ? 'visible' : ''}`}
				style={filterChipsContainerCss(isVisible)}
			>
				<div className="flex items-center gap-2 min-w-0 max-w-full overflow-hidden">
					{visibleFilters.map(filter => (
						<FilterChip key={`${filter.key}-${filter.value}`} label={filter.label} onRemove={filter.onRemove} />
					))}
				</div>
				{hiddenCount > 0 && (
					<TooltipProvider delayDuration={0} skipDelayDuration={0}>
						<Tooltip>
							<TooltipTrigger asChild>
								<div className="shrink-0 text-xs font-medium text-secondary">+{hiddenCount} more</div>
							</TooltipTrigger>
							<TooltipContent>{hiddenFilters}</TooltipContent>
						</Tooltip>
					</TooltipProvider>
				)}
			</div>
			{/* Hidden measurement div */}
			<div
				ref={measureRef}
				aria-hidden="true"
				className="fixed -left-full invisible whitespace-nowrap text-xs font-medium"
			/>
		</React.Fragment>
	);
};

export default FilterChipsContainer;
